import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog } from 'radix-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MAX_FREE_TEXT_LENGTH, MAX_NAME_LENGTH, type AllergyResponse } from '@emr/shared';

import { createAllergy } from '../../../api/chart.ts';
import { errorMessage, isContractMismatch } from '../../../api/client.ts';
import { FailureNotice } from '../../../components/AsyncState.tsx';
import { Button, ErrorText, Field, TextAreaField } from '../../../components/primitives.tsx';

export interface AddAllergyDialogProps {
  readonly patientId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSaved: (allergy: AllergyResponse) => void;
}

interface FormState {
  readonly substance: string;
  readonly reaction: string;
  readonly severity: string;
  readonly status: string;
}

const EMPTY_FORM: FormState = { substance: '', reaction: '', severity: '', status: '' };

/**
 * `AllergiesTab.tsx`'s own create form — the first write path wired into the chart-review UI
 * (`api/chart.ts`'s own header). Modeled on `schedule/AppointmentFormDialog.tsx`'s shape (Radix
 * `Dialog`, one `useMutation`, the same `isContractMismatch`/`FailureNotice` split for a real
 * server error vs. a client/server shape mismatch), simplified for a single create-only form with
 * no patient picker — the patient is already the page this tab lives on.
 */
export function AddAllergyDialog({
  patientId,
  open,
  onOpenChange,
  onSaved,
}: AddAllergyDialogProps) {
  const { t } = useTranslation('patientChartTabs');
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  // Re-seed on every open, not just once — otherwise closing after a save and reopening would show
  // the just-submitted values instead of a blank form, the same reasoning
  // `AppointmentFormDialog.tsx`'s own `seededFor` comment gives for its identical re-seed guard.
  const [seededForOpen, setSeededForOpen] = useState(false);
  if (!open && seededForOpen) {
    setSeededForOpen(false);
  } else if (open && !seededForOpen) {
    setForm(EMPTY_FORM);
    setFormError(null);
    setSeededForOpen(true);
  }

  const create = useMutation({
    mutationFn: () =>
      createAllergy(patientId, {
        substance: form.substance.trim(),
        reaction: form.reaction.trim().length > 0 ? form.reaction.trim() : undefined,
        severity: form.severity.trim().length > 0 ? form.severity.trim() : undefined,
        status: form.status.trim().length > 0 ? form.status.trim() : undefined,
        confidentialityReasons: [],
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['patient', patientId, 'allergies'] });
      onSaved(created);
      onOpenChange(false);
    },
  });

  function handleSubmit() {
    setFormError(null);
    if (form.substance.trim().length === 0) {
      setFormError(t('addAllergyDialog.substanceRequired'));
      return;
    }
    create.mutate();
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 z-50 flex max-h-[90vh] w-[min(28rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-y-auto rounded-panel border border-line bg-surface p-4 shadow-overlay sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <Dialog.Title className="m-0 text-lg font-semibold text-ink">
              {t('addAllergyDialog.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <Button variant="secondary" aria-label={t('addAllergyDialog.close')}>
                {t('addAllergyDialog.close')}
              </Button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">
            {t('addAllergyDialog.description')}
          </Dialog.Description>

          <Field
            label={t('addAllergyDialog.substanceLabel')}
            value={form.substance}
            onChange={(event) => {
              setForm((current) => ({ ...current, substance: event.target.value }));
            }}
            maxLength={MAX_NAME_LENGTH}
            placeholder={t('addAllergyDialog.substancePlaceholder')}
            required
          />

          <TextAreaField
            label={t('addAllergyDialog.reactionLabel')}
            value={form.reaction}
            onChange={(event) => {
              setForm((current) => ({ ...current, reaction: event.target.value }));
            }}
            maxLength={MAX_FREE_TEXT_LENGTH}
            placeholder={t('addAllergyDialog.reactionPlaceholder')}
            rows={3}
          />

          <Field
            label={t('addAllergyDialog.severityLabel')}
            value={form.severity}
            onChange={(event) => {
              setForm((current) => ({ ...current, severity: event.target.value }));
            }}
            maxLength={MAX_NAME_LENGTH}
            placeholder={t('addAllergyDialog.severityPlaceholder')}
          />

          <Field
            label={t('addAllergyDialog.statusLabel')}
            value={form.status}
            onChange={(event) => {
              setForm((current) => ({ ...current, status: event.target.value }));
            }}
            maxLength={MAX_NAME_LENGTH}
            placeholder={t('addAllergyDialog.statusPlaceholder')}
          />

          {formError !== null ? <ErrorText message={formError} /> : null}
          {create.error !== null ? (
            isContractMismatch(create.error) ? (
              <FailureNotice error={create.error} />
            ) : (
              <ErrorText message={errorMessage(create.error)} />
            )
          ) : null}

          <div className="flex justify-end gap-3">
            <Button pending={create.isPending} onClick={handleSubmit}>
              {create.isPending ? t('addAllergyDialog.submitting') : t('addAllergyDialog.submit')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
